/**
 * The explorer's transport: what the desktop's host bridge gives the workbench's Memory control, read here from the
 * project's memory routes, progressively, so a memory of ten thousand items or more paints at once and fills in.
 *
 *   1. `GET …/memory/graph?limit=` — the first page, painted at once. A page's edges are only those whose two ends are
 *      in the same page, and items that share one seq can straddle pages, so this route is not walked further.
 *   2. When the page was full: `GET …/memory/changes?since=0` names every item this person may read, a page at a time
 *      (a page never splits a seq), and `POST …/memory/items/batch` fetches the ones not yet held, 500 at a time, a few
 *      at once. The picture is rebuilt at most every {@link COMMIT_MS} while they arrive.
 *   3. Then `…/memory/changes?since=<seq>` every {@link POLL_MS} while the page is shown keeps it current: changed ids are
 *      fetched again, removed ones dropped, and a `reset` (history before `since` is gone) loads it all again.
 *
 * The picture is built in a worker (memoryExplorer.worker.ts), so the page's frames keep coming while it is. Every route
 * answers only what the signed-in person may read, so nothing here filters.
 */
import { MEMORY_BATCH_MAX_IDS, type MemoryItem } from '@aioe/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { problemText, routes } from '../../api/client.ts';
import type { FromWorker, ToWorker } from './memoryExplorer.worker.ts';
import { explorerItem, type ExplorerPicture } from './memoryExplorerModel.ts';

/** The first page: enough for most projects in one answer. */
export const FIRST_PAGE = 5000;
export const POLL_MS = 30_000;
export const COMMIT_MS = 1500;
/** Batch reads in flight at once while filling. */
const PARALLEL = 3;

export interface ExplorerData {
  /** The picture, once the first page is built. */
  picture?: ExplorerPicture;
  /** Until the first page has answered. */
  loading: boolean;
  /** Still fetching the rest after the first page. */
  filling: boolean;
  /** How many items the changes feed has named so far, while filling. */
  known?: number;
  /** Items kept from this person by their labels (owners hear it; others get 0), when the first page was the whole memory. */
  itemsFiltered: number;
  error?: string;
  /** When the first page and the whole reached the picture, and how long the last build in the worker took, ms. */
  firstMs?: number;
  allMs?: number;
  buildMs?: number;
}

const EMPTY: ExplorerData = { loading: true, filling: false, itemsFiltered: 0 };
/** In a commit's patch: stamp this field with the time the build answers. */
const STAMP = -1;

const pairs = (m: ReadonlyMap<string, string>) => [...m.entries()];

export function useMemoryExplorerData(projectId: string, rootNames: ReadonlyMap<string, string>): ExplorerData & { itemOf: (id: string) => MemoryItem | undefined } {
  const [state, setState] = useState<ExplorerData>(EMPTY);
  // Another project: start again from nothing (the effect below loads it).
  const [forProject, setForProject] = useState(projectId);
  if (forProject !== projectId) {
    setForProject(projectId);
    setState(EMPTY);
  }
  /** The items held, whole, for the card: read when something is selected, never drawn from. */
  const held = useRef(new Map<string, MemoryItem>());
  const roots = useRef(rootNames);
  /** Set by the loading effect: hand the worker new repository names and rebuild. */
  const renamed = useRef<((names: ReadonlyMap<string, string>) => void) | null>(null);

  useEffect(() => {
    let stopped = false;
    let poll: ReturnType<typeof setTimeout> | undefined;
    const map = new Map<string, MemoryItem>();
    held.current = map;
    const w = new Worker(new URL('./memoryExplorer.worker.ts', import.meta.url), { type: 'module' });
    const send = (m: ToWorker) => w.postMessage(m);
    send({ type: 'reset', rootNames: pairs(roots.current) });

    let lastCommit = 0;
    let seq = 0;
    const t0 = performance.now();
    // One build at a time; asking while one is out builds once more when it answers, with both patches.
    let ticket = 0;
    let waiting: Partial<ExplorerData> | null = null;
    let again: Partial<ExplorerData> | null = null;
    const build = (patch: Partial<ExplorerData>) => {
      if (waiting) {
        again = { ...(again ?? {}), ...patch };
        return;
      }
      waiting = patch;
      send({ type: 'build', ticket: ++ticket });
    };
    w.onmessage = (e: MessageEvent<FromWorker>) => {
      if (stopped || !waiting) return;
      const done = waiting;
      waiting = null;
      const at = Math.round(performance.now() - t0);
      setState((s) => ({
        ...s,
        ...done,
        ...(e.data.picture ? { picture: e.data.picture } : {}),
        buildMs: Math.round(e.data.ms),
        ...(done.firstMs === STAMP ? { firstMs: at } : {}),
        ...(done.allMs === STAMP ? { allMs: at } : {}),
      }));
      if (again) {
        const next = again;
        again = null;
        build(next);
      }
    };
    w.onerror = (e) => {
      if (!stopped) setState((s) => ({ ...s, loading: false, filling: false, error: e.message || 'The picture could not be drawn.' }));
    };
    renamed.current = (names) => {
      send({ type: 'roots', rootNames: pairs(names) });
      build({});
    };

    /** Rebuild the picture from what is held; `force` skips the throttle (the first page, the end of the fill). */
    const commit = (patch: Partial<ExplorerData>, force = false) => {
      if (stopped) return;
      const now = performance.now();
      if (!force && now - lastCommit < COMMIT_MS) {
        if (patch.known !== undefined) setState((s) => (s.known === patch.known ? s : { ...s, known: patch.known }));
        return;
      }
      lastCommit = now;
      build(patch);
    };
    const put = (items: readonly MemoryItem[]) => {
      for (const i of items) {
        if (i.deletedAt) map.delete(i.id);
        else map.set(i.id, i);
      }
      send({ type: 'put', items: items.map(explorerItem) });
    };
    const remove = (ids: string[]) => {
      for (const id of ids) map.delete(id);
      if (ids.length) send({ type: 'remove', ids });
    };
    /** Fetch these ids in batches the route takes, a few at once. */
    const fetchIds = async (ids: string[], onBatch?: () => void) => {
      const chunks: string[][] = [];
      for (let at = 0; at < ids.length; at += MEMORY_BATCH_MAX_IDS) chunks.push(ids.slice(at, at + MEMORY_BATCH_MAX_IDS));
      const lane = async () => {
        for (let c = chunks.shift(); c && !stopped; c = chunks.shift()) {
          const out = await routes.memoryItemsBatch(projectId, c);
          if (stopped) return;
          put(out.items);
          onBatch?.();
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, chunks.length) }, lane));
    };

    const fill = async () => {
      // Every readable id, a page of the changes feed at a time; what is not held yet is fetched as the pages come.
      let since = 0;
      const named = new Set<string>();
      for (;;) {
        const page = await routes.memoryChanges(projectId, since);
        if (stopped) return;
        for (const c of page.changed) named.add(c.id);
        remove(page.removed);
        const missing = page.changed.map((c) => c.id).filter((id) => !map.has(id));
        await fetchIds(missing, () => commit({ known: named.size }));
        since = page.seq;
        if (!page.more) break;
      }
      // What the first page held that the feed no longer names was removed, or is no longer readable.
      remove([...map.keys()].filter((id) => !named.has(id)));
      seq = Math.max(seq, since);
      commit({ filling: false, known: undefined, allMs: STAMP }, true);
    };

    const keepCurrent = async () => {
      if (stopped) return;
      if (!document.hidden) {
        try {
          for (;;) {
            const page = await routes.memoryChanges(projectId, seq);
            if (stopped) return;
            if (page.reset) {
              remove([...map.keys()]);
              seq = 0;
              await fill();
              break;
            }
            remove(page.removed);
            const ids = page.changed.map((c) => c.id);
            if (ids.length) await fetchIds(ids);
            seq = page.seq;
            if (ids.length || page.removed.length) commit({ error: undefined }, true);
            if (!page.more) break;
          }
        } catch (e) {
          if (!stopped) setState((s) => ({ ...s, error: problemText(e) }));
        }
      }
      if (!stopped) poll = setTimeout(() => void keepCurrent(), POLL_MS);
    };

    void (async () => {
      try {
        const first = await routes.memoryGraphPage(projectId, FIRST_PAGE);
        if (stopped) return;
        put(first.items);
        seq = first.seq;
        commit({ loading: false, filling: Boolean(first.more), itemsFiltered: first.more ? 0 : first.itemsFiltered, firstMs: STAMP, ...(first.more ? {} : { allMs: STAMP }) }, true);
        if (first.more) await fill();
      } catch (e) {
        if (!stopped) setState((s) => ({ ...s, loading: false, filling: false, error: problemText(e) }));
      }
      if (!stopped) poll = setTimeout(() => void keepCurrent(), POLL_MS);
    })();

    return () => {
      stopped = true;
      renamed.current = null;
      if (poll) clearTimeout(poll);
      w.terminate();
    };
  }, [projectId]);

  // The repositories' names changed (an owner renamed one): the picture's topics follow.
  useEffect(() => {
    if (roots.current === rootNames) return;
    roots.current = rootNames;
    renamed.current?.(rootNames);
  }, [rootNames]);

  const itemOf = useCallback((id: string) => held.current.get(id), []);
  return { ...state, itemOf };
}
